
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AlertTriangle, ArrowRight, TrendingDown, TrendingUp, Users, BarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const RiskMetricCard = ({ title, value, trend, description, icon: Icon }) => {
  const isTrendUp = trend === "up";
  const TrendIcon = isTrendUp ? TrendingUp : TrendingDown;
  const trendColor = isTrendUp ? "text-green-500" : "text-red-500";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <div className="flex items-center mt-1 text-xs">
          <TrendIcon className={`h-3 w-3 mr-1 ${trendColor}`} />
          <span className={trendColor}>{trend === "up" ? "↑" : "↓"} {Math.abs(trend)}%</span>
          <span className="text-muted-foreground ml-2">{description}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const RiskAssessment = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Risk Assessment</h1>
        <AlertTriangle className="h-8 w-8 text-primary" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RiskMetricCard
          title="Portfolio Risk Score"
          value="68/100"
          trend={-2.5}
          description="vs last month"
          icon={AlertTriangle}
        />
        <RiskMetricCard
          title="Default Rate"
          value="3.2%"
          trend={0.5}
          description="vs last month"
          icon={TrendingDown}
        />
        <RiskMetricCard
          title="Avg. Borrower Score"
          value="720"
          trend={1.8}
          description="vs last month"
          icon={Users}
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="borrowers">Borrowers</TabsTrigger>
          <TabsTrigger value="sectors">Sectors</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Risk Distribution</CardTitle>
                <CardDescription>Loan portfolio by risk category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Low Risk (AAA-A)</span>
                      <span className="text-sm font-medium">42%</span>
                    </div>
                    <Progress value={42} className="h-2 bg-slate-200" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Medium Risk (BBB-B)</span>
                      <span className="text-sm font-medium">35%</span>
                    </div>
                    <Progress value={35} className="h-2 bg-slate-200" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">High Risk (CCC-C)</span>
                      <span className="text-sm font-medium">18%</span>
                    </div>
                    <Progress value={18} className="h-2 bg-slate-200" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Default Risk (D)</span>
                      <span className="text-sm font-medium">5%</span>
                    </div>
                    <Progress value={5} className="h-2 bg-slate-200" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Risk Trends</CardTitle>
                <CardDescription>6-month historical risk analysis</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center justify-center">
                <div className="text-center py-8">
                  <BarChart className="h-16 w-16 text-muted-foreground mx-auto" />
                  <p className="mt-4 text-muted-foreground">
                    Detailed risk trend charts would appear here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="borrowers" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Borrower Risk Analysis</CardTitle>
              <CardDescription>Assessment by borrower segments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Users className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Detailed borrower risk analysis would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="sectors" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Sector Risk Analysis</CardTitle>
              <CardDescription>Assessment by industry sectors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <BarChart className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Detailed sector risk analysis would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button className="flex items-center gap-2">
          Run AI Risk Analysis <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RiskAssessment;
